import React, { useMemo } from "react";
import { Button, DownloadIcon, Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import DirectoryProvider from "~/pages/Directory/shared/DirectoryProvider";
import DirectoryRowActions from "~/pages/Directory/shared/DirectoryRowActions";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetBaseTariffsList } from "~/entity/Tariffs";
import { TariffsService } from "~/entity/Tariffs/api/tariffsService";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { matchesText } from "~/pages/Directory/shared/filterHelpers";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { sortItems, TableV2, useURLTableSort } from "~/shared/ui/TableV2";

import BaseTariffFormModal from "./BaseTariffFormModal";
import BaseTariffsFilter from "./BaseTariffsFilter";
import { IBaseTariffRow, mapBaseTariffApiToRow } from "./model/baseTariffRow";
import {
  IBaseTariffsUrlParams,
  mapBaseTariffsFilterToQuery,
  parseBaseTariffsFilterFromUrl,
} from "./model/filterBaseTariffs";
import { getBaseTariffsColumns } from "./model/tableAdapter";
import { BASE_TARIFFS_OPTIONS } from "./options";

import s from "~/pages/Directory/shared/Directory.less";

const BaseTariffs = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasPermission } = usePermissions();
  const canEditTariff = hasPermission("employeeAccounting.tariffs.id.put");
  const canExport = hasPermission("employeeAccounting.tariffs.export.get");
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<IBaseTariffsUrlParams>();
  const { page, count = 25, sortField, sortType: sortTypeRaw } = urlParams;

  const sortableColumns = BASE_TARIFFS_OPTIONS;

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns,
    setParams,
  });

  const tableSortField =
    sortField && sortableColumns.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const formFilter = useMemo(
    () => parseBaseTariffsFilterFromUrl(urlParams),
    [urlParams],
  );

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  const queryParams = useMemo(
    () => mapBaseTariffsFilterToQuery(pageNumber, pageSize, formFilter),
    [pageNumber, pageSize, formFilter],
  );

  const {
    tariffs: apiTariffs,
    total,
    isLoading,
  } = useGetBaseTariffsList(queryParams);
  const { tariffs: optionTariffs, isLoading: isPositionOptionsLoading } =
    useGetBaseTariffsList({
      page: 0,
      count: 100,
    });

  const rows = useMemo<IBaseTariffRow[]>(
    () => apiTariffs.map(mapBaseTariffApiToRow),
    [apiTariffs],
  );

  const positionOptions = useMemo(
    () =>
      optionTariffs
        .map((tariff) => ({
          value: String(tariff.instanceId),
          position: tariff.positions?.[0],
        }))
        .filter((item) => item.position)
        .map((item) => ({
          value: item.value,
          label: `${item.position!.code}. ${item.position!.name}`,
        })),
    [optionTariffs],
  );

  const handleRowEdit = (record: { id?: string | number }) => {
    setParams({ openModal: "Edit", currentId: String(record.id) });
  };

  const filtered = useMemo(
    () => rows.filter((row) => matchesText(row.author, formFilter.author)),
    [rows, formFilter.author],
  );

  const sorted = useMemo(
    () => sortItems(filtered, tableSortField, normalizedSortType),
    [filtered, tableSortField, normalizedSortType],
  );

  const columns = useMemo(
    () =>
      getBaseTariffsColumns(
        t,
        tableSortField,
        normalizedSortType,
        sortableColumns,
      ),
    [t, tableSortField, normalizedSortType, sortableColumns],
  );

  const paginationLabels = useMemo(
    () => ({
      totalRows: (total: number) =>
        t("directory.baseTariffs.pagination.totalRows", { count: total }),
    }),
    [t],
  );

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const handleDownload = () => {
    const { page: _page, count: _count, ...filters } = queryParams;

    document.location.href = TariffsService.GET.exportUrl(filters);
  };

  const goBack = () => history.replace("/editor/");

  return (
    <DirectoryProvider>
      <Wrapper.Content
        title={t("directory.baseTariffs.title")}
        txtBtn={t("ui.back")}
        goBackHandler={goBack}
        filterComponent={
          <BaseTariffsFilter
            positionOptions={positionOptions}
            positionOptionsLoading={isPositionOptionsLoading}
          />
        }
      >
        {canExport && (
          <div className={s.downloadToolbar}>
            <Button
              bold
              color="secondary"
              variant="filled"
              fontSize="10"
              size="small"
              radius={30}
              icon={<DownloadIcon size={12} />}
              onClick={handleDownload}
            >
              {t("directory.download")}
            </Button>
          </div>
        )}

        <TableV2
          wrapperClassName={s.tableWrapper}
          columns={columns}
          dataSource={sorted}
          rowKey="id"
          scroll={{ x: "max-content" }}
          actionsFixed
          actionsPlacement="left"
          sortableColumns={sortableColumns}
          sortField={tableSortField}
          sortType={tableSortField ? normalizedSortType : undefined}
          onSortChange={onSortChange}
          loading={isLoading}
          rowActions={(record) => (
            <DirectoryRowActions
              record={record}
              rowKey={record.id}
              onRowEdit={canEditTariff ? handleRowEdit : undefined}
            />
          )}
        />

        <Pagination
          count={pageSize}
          page={pageNumber + 1}
          totalCount={total}
          onChangeCount={handleCount}
          onChangePage={handlePage}
          selectFrom={[25, 50, 100]}
          labels={paginationLabels}
        />
        <BaseTariffFormModal rows={rows} />
      </Wrapper.Content>
    </DirectoryProvider>
  );
};

export default BaseTariffs;
